import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Groups as GroupsIcon,
  MenuBook as MenuBookIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import GlossaryTerm from '../common/GlossaryTerm';
import GLOSSARY from '../../constants/glossary';

// Beginner glossary rendered at the bottom of this modal — sourced from the
// shared glossary so it's the same data the inline <GlossaryTerm> tooltips
// above use, not a second copy that can drift out of sync.
const glossary = [
  ['Repo', GLOSSARY.repo],
  ['Commit', GLOSSARY.commit],
  ['Push', GLOSSARY.push],
  ['Branch', GLOSSARY.branch],
  ['Pull request', GLOSSARY['pull request']],
  ['README', GLOSSARY.readme],
  ['GitHub PAT', GLOSSARY['github pat']],
  ['Collaborator', GLOSSARY.collaborator],
];

const repoSteps = [
  'Create a GitHub account.',
  'Create your personal learning repo.',
  <>Add your mentor as a <GlossaryTerm term="collaborator">collaborator</GlossaryTerm> on that personal repo — this gives them permission to view and comment on your work.</>,
  <>
    <GlossaryTerm term="clone">Clone</GlossaryTerm> your personal repo into VS Code after installing Git and VS Code.
  </>,
  'Create folders: notes, datasets, dashboards, sql, python, screenshots, portfolio.',
  <>
    In Qode Clarity Account profile, save your personal repo URL and a fine-grained{' '}
    <GlossaryTerm term="github pat">GitHub PAT</GlossaryTerm>.
  </>,
  <>
    Use "Push journal to GitHub" in lessons to update{' '}
    <GlossaryTerm term="journal.md">journal.md</GlossaryTerm> and keep README linked to it.
  </>,
];

const cohortSteps = [
  'Accept the GitHub invite.',
  'Use the cohort repo for hackathons.',
  <>
    Work on a <GlossaryTerm term="branch">branch</GlossaryTerm>, open a{' '}
    <GlossaryTerm term="pull request">pull request</GlossaryTerm>, and respond to review comments.
  </>,
];

const cadence = [
  ['Daily tasks', 'Lesson tasks and saved evidence.'],
  ['Concept clinic', 'Core ideas, assumptions, and worked examples.'],
  ['Weekly hackathon', 'Shared practical challenge and review.'],
  ['Show and tell', 'Present work and get feedback.'],
  ['Mock interviews', 'Practise with portfolio evidence.'],
  ['Microsoft workspace', 'Documents, spreadsheets, and collaboration.'],
  ['Evidence intelligence', <>
    <GlossaryTerm term="journal coach">Journal Coach</GlossaryTerm> and{' '}
    <GlossaryTerm term="evidence intelligence">Evidence Intelligence</GlossaryTerm>.
  </>],
  ['Microsoft labs', 'Azure, Fabric, Power BI, or related tools where relevant.']
];

const pathways = [
  ['12-month mentored path', 'Steady pace', 'Support, repetition, portfolio building, and interview practice.'],
  ['6-month intensive path', 'Faster pace', 'Faster portfolio and interview readiness.']
];

const formats = [
  ['Small cohort', '5-10 learners', 'Peer learning, hackathons, reviews, and shared accountability.'],
  ['Personalised coach', '1 learner', 'Private pacing, direct feedback, and focused portfolio support.']
];

const includedPackage = [
  ['Learning platform', 'Lessons, tasks, journal, evidence, and progress.'],
  ['Guided tools', 'Microsoft 365, Azure, Power BI, or other cohort tools.'],
  ['Portfolio evidence', 'Repo, README, screenshots, dashboards, SQL, Python, and notes.'],
  ['Mentored review', 'Clinics, hackathons, show-and-tell, interviews, and feedback.']
];

const azureNavigation = [
  [
    'Weeks 1-4',
    'Python, SQL, modelling, storage, ADF/Fabric basics, and data movement.'
  ],
  [
    'Weeks 5-10',
    <>
      Pipelines plus production controls:{' '}
      <GlossaryTerm term="key vault">Key Vault</GlossaryTerm>,{' '}
      <GlossaryTerm term="managed identity">Managed Identity</GlossaryTerm>,{' '}
      <GlossaryTerm term="rbac">RBAC</GlossaryTerm>, monitoring, alerts, and cost controls.
    </>
  ],
  [
    'Weeks 11-16+',
    <>
      Landing zones, governance, networking, <GlossaryTerm term="ci/cd">CI/CD</GlossaryTerm>,
      Terraform/Bicep, observability, and operations.
    </>
  ],
  [
    'Final portfolio',
    'Secure data pipeline, deployment, monitoring, README, architecture diagram, and runbook.'
  ]
];

const LearnerOnboardingModal = ({ open, onClose, user }) => {
  const theme = useTheme();
  const paths = Array.isArray(user?.learning_paths) ? user.learning_paths : [];
  const showAzureGuidance = paths.some((path) =>
    ['azure', 'azure-platform', 'azure-data-engineer', 'azure-platform-engineer'].includes(path)
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 20px)', sm: '100%' },
          m: { xs: 1.25, sm: 4 },
          maxHeight: { xs: 'calc(100% - 20px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1.25,
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: { xs: 34, sm: 40 },
              height: { xs: 34, sm: 40 },
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.1)
            }}
          >
            <GroupsIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', sm: '1.5rem' } }}>Learner onboarding</Typography>
            <Typography variant="body2" color="text.secondary">
              How the Qode Clarity mentored career accelerator works.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 }, overflowX: 'hidden' }}>
        <Stack spacing={{ xs: 2, sm: 3 }}>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            }}
          >
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
              <Chip label="Mentored career accelerator" color="primary" />
              <Chip label="Small cohort or 1:1 coaching" variant="outlined" />
              <Chip label="Build + review + collaborate" variant="outlined" />
              <Chip label="Portfolio evidence first" variant="outlined" />
            </Stack>
            <Typography color="text.secondary" sx={{ lineHeight: 1.65 }}>
              Build portfolio evidence through lessons, mentor feedback, projects, interviews, and shared cohort work.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Learning formats</Typography>
            <Grid container spacing={1.25}>
              {formats.map(([label, size, description]) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{label}</Typography>
                      <Chip label={size} size="small" color="primary" variant="outlined" />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">{description}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Program pathways</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
              Choose the pace that fits your goals and weekly availability.
            </Typography>
            <Grid container spacing={1.25}>
              {pathways.map(([label, pace, description]) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{label}</Typography>
                      <Chip label={pace} size="small" color="primary" variant="outlined" />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">{description}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>What is included</Typography>
            <Grid container spacing={1.25}>
              {includedPackage.map(([label, description]) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{label}</Typography>
                    <Typography variant="body2" color="text.secondary">{description}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, minWidth: 0 }}>
                  <MenuBookIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.25rem' } }}>Your individual repo</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Your daily work, journal notes, screenshots, SQL, dashboards, Python, and project READMEs.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, minWidth: 0 }}>
                  <AccountTreeIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.25rem' } }}>The cohort repo</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Shared cohort work for hackathons, branches, pull requests, reviews, and collaboration.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Set up your personal repo</Typography>
            <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 800, mb: 0.5 }}>
              1. Set up GitHub
            </Typography>
            <Stack component="ol" spacing={1} sx={{ pl: 3, m: 0 }}>
              {repoSteps.slice(0, 4).map((step, i) => (
                <Typography key={i} component="li" variant="body2" color="text.secondary">
                  {step}
                </Typography>
              ))}
            </Stack>
            <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 800, mb: 0.5, mt: 1.5 }}>
              2. Connect it to Qode Clarity
            </Typography>
            <Stack component="ol" spacing={1} sx={{ pl: 3, m: 0 }} start={5}>
              {repoSteps.slice(4).map((step, i) => (
                <Typography key={i} component="li" variant="body2" color="text.secondary">
                  {step}
                </Typography>
              ))}
            </Stack>
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <SettingsIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>How it works</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Add your repo URL and <GlossaryTerm term="github pat">GitHub PAT</GlossaryTerm> once.
                Then use "Push to GitHub" to update <GlossaryTerm term="journal.md">journal.md</GlossaryTerm>.
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Use the cohort repo</Typography>
            <Stack component="ol" spacing={1} sx={{ pl: 3, m: 0 }}>
              {cohortSteps.map((step, i) => (
                <Typography key={i} component="li" variant="body2" color="text.secondary">
                  {step}
                </Typography>
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Events and program structure</Typography>
            <Grid container spacing={1.25}>
              {cadence.map(([label, description]) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{label}</Typography>
                    <Typography variant="body2" color="text.secondary">{description}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {showAzureGuidance && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>For Azure learners: use both tracks without overload</Typography>
                <Chip label="Azure Data Engineer + Azure Platform Engineer" size="small" color="primary" variant="outlined" />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Data Engineering first. Platform Engineering makes the same work secure, deployable, and observable.
              </Typography>
              <Grid container spacing={1.25}>
                {azureNavigation.map(([label, description]) => (
                  <Grid item xs={12} sm={6} key={label}>
                    <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{label}</Typography>
                      <Typography variant="body2" color="text.secondary">{description}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: alpha(theme.palette.success.main, 0.08) }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Daily and weekly rule</Typography>
                <Typography variant="body2" color="text.secondary">
                  Daily lesson evidence. Weekly proof package. One clear repo update.
                </Typography>
              </Box>
            </Box>
          )}

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Beginner glossary</Typography>
            <Grid container spacing={1.25}>
              {glossary.map(([term, meaning]) => (
                <Grid item xs={12} sm={6} key={term}>
                  <Typography variant="body2">
                    <strong>{term}:</strong> <Typography component="span" variant="body2" color="text.secondary">{meaning}</Typography>
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LearnerOnboardingModal;
